/**
 * TimesheetApprovalsPage - Refactored Version
 * Uses shared components and hooks.
 */
import { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { timesheetAPI } from '../lib/api';
import { useApi } from '../hooks/useApi';
import { useTable } from '../hooks/useTable';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Calendar, User, Loader2, Eye, FileText, ChevronRight } from 'lucide-react';

// Shared components
import { DataTable } from '../components/shared/DataTable';
import { SearchInput } from '../components/shared/SearchInput';
import { PageSkeleton, CardSkeleton } from '../components/shared/LoadingSkeleton';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function TimesheetApprovalsPage() {
  const { user, isAdmin, isHR } = useAuth();
  
  // State
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [weekDetails, setWeekDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Data fetching
  const { data: pendingApprovals, loading, refresh } = useApi(timesheetAPI.getPendingApprovals);

  // Table hook
  const { items: filteredApprovals, search, setSearch, sort, setSort } = useTable(pendingApprovals || [], {
    searchFields: ['employee_name'],
    defaultSort: { field: 'week_start', order: 'desc' }
  });

  // Stats
  const stats = useMemo(() => {
    const approvals = pendingApprovals || [];
    const totalHours = approvals.reduce((sum, w) => sum + (w.total_hours || 0), 0);
    return {
      pending: approvals.length,
      totalHours,
      avgHours: approvals.length ? (totalHours / approvals.length).toFixed(1) : 0
    };
  }, [pendingApprovals]);

  const viewWeekDetails = async (week) => {
    setSelectedWeek(week);
    setDetailsLoading(true);
    try {
      const res = await timesheetAPI.getWeek(week.week_start, week.employee_id);
      setWeekDetails(res.data);
    } catch (error) {
      toast.error('Failed to load week details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const approveWeek = async () => {
    if (!selectedWeek) return;
    setProcessing(true);
    try {
      await timesheetAPI.approveWeek(selectedWeek.week_start);
      toast.success('Timesheet approved');
      closeDetails();
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const rejectWeek = async () => {
    if (!selectedWeek || !rejectReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    setProcessing(true);
    try {
      await timesheetAPI.rejectWeek(selectedWeek.week_start, rejectReason);
      toast.success('Timesheet rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      closeDetails();
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const closeDetails = () => {
    setSelectedWeek(null);
    setWeekDetails(null);
  };

  // Table columns
  const columns = [
    { key: 'employee_name', label: 'Employee', sortable: true, render: (row) => (
      <div className="flex items-center gap-2">
        <User className="w-4 h-4 text-muted-foreground" />
        <span className="font-medium">{row.employee_name || 'Unknown'}</span>
      </div>
    )},
    { key: 'week_start', label: 'Week', sortable: true, render: (row) => (
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <span>{formatDate(row.week_start)}</span>
      </div>
    )},
    { key: 'total_hours', label: 'Hours', sortable: true, render: (row) => (
      <Badge variant={row.total_hours >= 40 ? 'default' : 'secondary'}>
        {row.total_hours?.toFixed(1) || 0}h
      </Badge>
    )},
    { key: 'status', label: 'Status', render: (row) => (
      <Badge variant="secondary" className="flex items-center gap-1 w-fit">
        <Clock className="w-3 h-3" />
        Pending Review
      </Badge>
    )},
    { key: 'actions', label: '', render: (row) => (
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); viewWeekDetails(row); }}>
          <Eye className="w-4 h-4 mr-1" /> Review
        </Button>
      </div>
    )}
  ];

  if (loading) return <PageSkeleton />;

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Timesheet Approvals</h1>
        <p className="text-muted-foreground mt-1">Review and approve team timesheets</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-md hover:border-primary/50 hover:scale-[1.02] transition-all"
          onClick={() => setSearch('')}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">Pending Review</span>
                </div>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-muted-foreground text-sm mb-1">Total Hours</div>
            <p className="text-2xl font-bold">{stats.totalHours.toFixed(1)}h</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-muted-foreground text-sm mb-1">Avg Hours/Week</div>
            <p className="text-2xl font-bold">{stats.avgHours}h</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <SearchInput value={search} onChange={setSearch} placeholder="Search by employee name..." className="max-w-md" />

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Pending Approvals
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <DataTable
            data={filteredApprovals}
            columns={columns}
            sort={sort}
            onSort={setSort}
            emptyMessage="No timesheets pending approval"
            emptyIcon={<CheckCircle className="h-12 w-12 text-green-500" />}
          />
        </CardContent>
      </Card>

      {/* Week Details Dialog */}
      <Dialog open={!!selectedWeek} onOpenChange={(open) => !open && closeDetails()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Timesheet Review</DialogTitle>
            <DialogDescription>
              {selectedWeek && `${selectedWeek.employee_name} - Week of ${formatDate(selectedWeek.week_start)}`}
            </DialogDescription>
          </DialogHeader>

          {detailsLoading ? (
            <CardSkeleton hasHeader={false} />
          ) : weekDetails ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold">{weekDetails.total_hours?.toFixed(1) || 0}h</p>
                  <p className="text-sm text-muted-foreground">Total Hours</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{weekDetails.entries?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Entries</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{weekDetails.projects?.length || 0}</p>
                  <p className="text-sm text-muted-foreground">Projects</p>
                </div>
              </div>

              {/* Entries */}
              {weekDetails.entries?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-2 text-left">Date</th>
                        <th className="px-4 py-2 text-left">Project</th>
                        <th className="px-4 py-2 text-left">Task</th>
                        <th className="px-4 py-2 text-right">Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {weekDetails.entries.map((entry, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-4 py-2">{formatDate(entry.date)}</td>
                          <td className="px-4 py-2">{entry.project_name || '-'}</td>
                          <td className="px-4 py-2">{entry.task_name || entry.description || '-'}</td>
                          <td className="px-4 py-2 text-right font-medium">{entry.hours}h</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeDetails}>Cancel</Button>
            <Button variant="destructive" onClick={() => setRejectDialogOpen(true)} disabled={processing}>
              <XCircle className="w-4 h-4 mr-1" /> Reject
            </Button>
            <Button onClick={approveWeek} disabled={processing}>
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Timesheet</DialogTitle>
            <DialogDescription>Provide a reason for rejection</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Enter reason..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={rejectWeek} disabled={processing || !rejectReason.trim()}>
              {processing && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
